import { shallowEqual } from "react-redux";
import {
    selectIsCalibration,
    selectIsEvaluationNotAssigned,
    selectIsNotAssigneeApplicable,
    selectIsPartialResponse,
    selectScorable,
} from "./scorableSlice";
import { useStrings } from "features/i18n/useStrings";
import strings from "./Scorable.strings";
import {
    selectVisibleQuestionGroupIds,
    selectVisibleQuestionIds,
} from "features/qualityForm/qualityFormSlice";
import {
    selectForm,
    selectFormType,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import FormType from "enums/FormType";
import {
    GuxIcon,
    GuxAccordion,
    GuxAccordionSection,
    GuxBadge,
    GuxTag,
} from "genesys-spark-components-react";
import "./Scorable.less";
import { DateTimeWithTimeZone } from "features/i18n/DateTime";
import {
    EvaluationSourceTypeEnum,
    EvaluationStatusEnum,
    SurveyStatusEnum,
    SurveySurveyTypeEnum,
} from "@genesys/cloud-frontend/dist/mjs/models";
import { ScorableQuestionGroup } from "./ScorableQuestionGroup";
import ScorableQuestion from "./ScorableQuestion";
import { FormLocaleProvider } from "features/i18n/useLocale";
import { useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import {
    HasEditScoreOrEditPermissionsContext,
    PreviewModeContext,
} from "features/qualityForm/common/Contexts";
import { useContext, useState } from "react";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import { GuxIconIconName } from "genesys-spark-components";
import EvaluationStatus from "enums/EvaluationStatus";
import { selectIsSystemSubmitted } from "./scorableSlices/scorableCommons";

type MetaData = keyof typeof strings.metadata.labels;
type ScorableStatus = keyof typeof strings.metadata.status;

enum CompletedEvaluationAdminMetaData {
    agent = "agent",
    assignee = "assignee",
    evaluator = "evaluator",
    createdDate = "createdDate",
    originalCreatedDate = "originalCreatedDate",
    revisionCreatedDate = "revisionCreatedDate",
    releasedDate = "releasedDate",
    modifiedDate = "modifiedDate",
    evaluationSource = "evaluationSource",
    version = "version",
    disputeCount = "disputeCount",
}

enum AgentMetaData {
    agent = "agent",
    version = "version",
    disputeCount = "disputeCount",
}

enum EditableEvaluationMetaData {
    agent = "agent",
    assignee = "assignee",
    evaluator = "evaluator",
    modifiedDate = "modifiedDate",
    evaluationSource = "evaluationSource",
    disputeCount = "disputeCount",
}

enum SurveyMetaData {
    agent = "agent",
    surveyType = "surveyType",
    modifiedDate = "modifiedDate",
}

export function ScorableMetaDataEntry({
    type,
    children,
    evaluationSourceType,
}: {
    type: MetaData;
    children: React.ReactNode;
    evaluationSourceType?: EvaluationSourceTypeEnum;
}) {
    const t = useStrings(strings);
    const label = evaluationSourceType
        ? t.metadata.evaluationSource[evaluationSourceType] ??
          t.metadata.evaluationSource.Unknown
        : t.metadata.labels[type];

    return children ? (
        <div className="ScorableMetaDataEntry" data-testid={`test-${type}`}>
            <span className="metadata-label">{label}</span>
            <span
                className="metadata-value"
                data-testid={`metadata-value-${type}`}
            >
                {children}
            </span>
        </div>
    ) : (
        <></>
    );
}

export function StatusIndicator({ status }: { status: ScorableStatus }) {
    const t = useStrings(strings);

    const icon: { [key in EvaluationStatus]: GuxIconIconName } = {
        pending: "radio",
        inprogress: "menu-kebab-horizontal",
        complete: "alert-success",
        inreview: "file-preview",
        retracted: "alert-warning-octogon",
        calibration: "graph-bar-vertical",
        partialResponse: "checkbox-partial",
    };

    const tagColor = status === EvaluationStatus.INPROGRESS ? "5" : undefined;

    const badgeColor =
        status === EvaluationStatus.COMPLETE
            ? "success"
            : status === EvaluationStatus.PENDING ||
              status === EvaluationStatus.INREVIEW
            ? "warning"
            : status === EvaluationStatus.RETRACTED
            ? "error"
            : "info";

    const statusContents = (
        <>
            <GuxIcon iconName={icon[status]} decorative={true} />
            <span className="form-status-label" data-testid="form-status-label">
                {t.metadata.status[status]}
            </span>
        </>
    );

    return status === EvaluationStatus.INPROGRESS ? (
        <GuxTag accent={tagColor} emphasis="subtle">
            {statusContents}
        </GuxTag>
    ) : (
        <GuxBadge accent={badgeColor}>{statusContents}</GuxBadge>
    );
}

export function ScorableStatus() {
    const status = useAppSelector((state) => {
        const formType = selectFormType(state);
        if (formType === FormType.ASSESSMENT) {
            return;
        }

        const status = selectScorable(state)?.status;

        switch (status) {
            case EvaluationStatusEnum.FINISHED:
            case SurveyStatusEnum.Finished:
                return EvaluationStatus.COMPLETE;
            case EvaluationStatusEnum.INPROGRESS:
            case SurveyStatusEnum.InProgress:
                return EvaluationStatus.INPROGRESS;
            case EvaluationStatusEnum.PENDING:
            case SurveyStatusEnum.Pending:
                return EvaluationStatus.PENDING;
            case EvaluationStatusEnum.INREVIEW:
                return EvaluationStatus.INREVIEW;
            case EvaluationStatusEnum.RETRACTED:
                return EvaluationStatus.RETRACTED;
        }
    });

    const isCalibration = useAppSelector(selectIsCalibration);

    const isPartialResponse = useAppSelector(selectIsPartialResponse);
    const showPartialResponse =
        useAppSelector(selectFormType) === FormType.SURVEY && isPartialResponse;

    return (
        <div className="ScorableStatus">
            {isCalibration && <StatusIndicator status="calibration" />}
            {status && <StatusIndicator status={status} />}
            {showPartialResponse && (
                <StatusIndicator status="partialResponse" />
            )}
        </div>
    );
}

export function ScorableMetaData({
    context,
}: {
    context: "admin" | "agent" | undefined;
}) {
    const t = useStrings(strings);
    const [isOpen, setIsOpen] = useState(false);

    const isEvaluationAssigneeFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
        );
    });
    const isRevisionCreatedDateFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(
            state,
            FeatureToggle.REVISION_CREATED_DATE_TOGGLE
        );
    });
    const isNotAssigned = useAppSelector(selectIsEvaluationNotAssigned);
    const isNotAssigneeApplicable = useAppSelector(
        selectIsNotAssigneeApplicable
    );
    const isCalibration = useAppSelector(selectIsCalibration);
    const isEvaluation = useAppSelector(selectFormType) === FormType.EVALUATION;
    const isSurvey = useAppSelector(selectFormType) === FormType.SURVEY;
    const isSystemSubmitted = useAppSelector(selectIsSystemSubmitted);
    const { metadata, evaluationSourceType } = useAppSelector((state) => {
        const scorable = selectScorable(state);
        const qualityForm = selectForm(state);
        const isEvaluationNotCalibration =
            isEvaluationAssigneeFeatureEnabled &&
            !isCalibration &&
            isEvaluation;
        const showRevisionCreatedDate =
            !isCalibration &&
            isEvaluation &&
            isRevisionCreatedDateFeatureEnabled;
        const showOriginalCreatedDate =
            showRevisionCreatedDate && scorable?.revisionCreatedDate != null;
        const isScorableFinished =
            scorable?.status !== "PENDING" && scorable?.status !== "INPROGRESS";
        const shouldShowEvaluatorName =
            isEvaluationNotCalibration || isScorableFinished;
        const evaluator =
            (shouldShowEvaluatorName &&
                (isSystemSubmitted
                    ? t.metadata.virtualSupervisor
                    : scorable?.evaluator?.name ?? t.metadata.unknown)) ||
            null;
        return {
            metadata: {
                agent: scorable?.agent?.name ?? t.metadata.unknown,
                surveyType: isSurvey
                    ? scorable?.surveyType === SurveySurveyTypeEnum.Voice
                        ? t.metadata.voice
                        : t.metadata.web
                    : null,
                assignee: isEvaluationNotCalibration
                    ? isNotAssigneeApplicable
                        ? t.metadata.notApplicable
                        : isNotAssigned
                        ? t.metadata.unassigned
                        : scorable?.assignee?.name ?? t.metadata.unknown
                    : null,
                evaluator: evaluator,
                createdDate: showOriginalCreatedDate
                    ? null
                    : scorable?.assignedDate,
                originalCreatedDate: showOriginalCreatedDate
                    ? scorable?.assignedDate
                    : null,
                revisionCreatedDate: showRevisionCreatedDate
                    ? scorable?.revisionCreatedDate
                    : null,
                releasedDate: scorable?.releaseDate,
                modifiedDate: qualityForm?.modifiedDate,
                evaluationSource:
                    scorable?.evaluationSource?.name ?? t.metadata.unknown,
                version: scorable?.status === "RETRACTED" && scorable?.version,
                disputeCount:
                    scorable?.disputeCount && scorable?.disputeCount > 0
                        ? scorable?.disputeCount
                        : null,
            },
            evaluationSourceType: scorable?.evaluationSource?.type as
                | EvaluationSourceTypeEnum
                | undefined,
        };
    }, shallowEqual);

    const metaDataEntries = Object.entries(metadata).map(([field, value]) => {
        let showLabel = false;
        if (isSurvey) {
            showLabel = Object.values(SurveyMetaData).includes(
                field as SurveyMetaData
            );
        } else if (context === "admin") {
            showLabel = Object.values(
                CompletedEvaluationAdminMetaData
            ).includes(field as CompletedEvaluationAdminMetaData);
        } else if (context === "agent") {
            showLabel = Object.values(AgentMetaData).includes(
                field as AgentMetaData
            );
        } else {
            showLabel = Object.values(EditableEvaluationMetaData).includes(
                field as EditableEvaluationMetaData
            );
        }
        const hideEvaluationSourceField =
            field === "evaluationSource" && evaluationSourceType === undefined;
        return (
            showLabel && (
                <ScorableMetaDataEntry
                    key={field}
                    type={field as MetaData}
                    evaluationSourceType={
                        field === "evaluationSource"
                            ? evaluationSourceType
                            : undefined
                    }
                >
                    {value instanceof Date ? (
                        <DateTimeWithTimeZone date={value} />
                    ) : hideEvaluationSourceField ? undefined : (
                        value
                    )}
                </ScorableMetaDataEntry>
            )
        );
    });

    return (
        <div className="ScorableMetaData" data-testid="scorable-meta-data">
            <GuxAccordion>
                <GuxAccordionSection
                    hidden={false}
                    open={isOpen}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <h1 slot="header">
                        {t.labels.details} <ScorableStatus />
                    </h1>
                    <div slot="content">{metaDataEntries}</div>
                </GuxAccordionSection>
            </GuxAccordion>
        </div>
    );
}

ScorableMetaData.defaultProps = {
    context: undefined,
};

export function ScorableQuestionListSurveys({
    questionGroupId,
}: {
    questionGroupId: string;
}) {
    const questionIds = useAppSelector(
        (state: RootState) => selectVisibleQuestionIds(state, questionGroupId),
        shallowEqual
    );
    return (
        <div className="ScorableQuestionListSurveys">
            {questionIds.map((questionId) => (
                <FormLocaleProvider>
                    <ScorableQuestion id={questionId} />
                </FormLocaleProvider>
            ))}
        </div>
    );
}

export function ScorableQuestionList({
    questionGroupId,
    allowEditing = false,
    canReviewResults = false,
}: {
    questionGroupId: string;
    allowEditing?: boolean;
    canReviewResults?: boolean;
}) {
    const { previewMode } = useContext(PreviewModeContext);
    const formType = useAppSelector((state) => {
        return selectFormType(state);
    });
    const questionIds = useAppSelector(
        (state) => selectVisibleQuestionIds(state, questionGroupId),
        shallowEqual
    );
    const status = useAppSelector((state) => {
        const formType = selectFormType(state);
        if (formType === FormType.ASSESSMENT) {
            return;
        }

        const status = selectScorable(state)?.status;

        switch (status) {
            case EvaluationStatusEnum.FINISHED:
            case SurveyStatusEnum.Finished:
                return "complete";
            case EvaluationStatusEnum.INPROGRESS:
            case SurveyStatusEnum.InProgress:
                return "inprogress";
            case EvaluationStatusEnum.PENDING:
            case SurveyStatusEnum.Pending:
                return "pending";
        }
    });
    const { hasEditScoreOrEditPermissions } = useContext(
        HasEditScoreOrEditPermissionsContext
    );
    const hideScore =
        (hasEditScoreOrEditPermissions === false && status !== "complete") ||
        (formType === FormType.ASSESSMENT && !previewMode);

    return (
        <ol className="ScorableQuestionList">
            {questionIds.map((questionId) => (
                <li className="ScorableQuestionListEntry" key={questionId}>
                    {formType === FormType.SURVEY ? (
                        <FormLocaleProvider>
                            <ScorableQuestion id={questionId} />
                        </FormLocaleProvider>
                    ) : (
                        <ScorableQuestion
                            id={questionId}
                            allowEditing={allowEditing}
                            hideScore={hideScore}
                            canReviewResults={canReviewResults}
                        />
                    )}
                </li>
            ))}
        </ol>
    );
}

export function ScorableQuestionGroupListSurveys() {
    const questionGroupIds = useAppSelector(selectVisibleQuestionGroupIds);
    return (
        <div className="ScorableQuestionGroupListSurveys">
            {questionGroupIds.map((questionGroupId) => (
                <div key={questionGroupId}>
                    <ScorableQuestionListSurveys
                        questionGroupId={questionGroupId}
                    />
                </div>
            ))}
        </div>
    );
}

export function ScorableQuestionGroupList({
    rescoringEvaluation = false,
    questionGroupToDisplay,
    canReviewResults = false,
}: {
    rescoringEvaluation?: boolean;
    questionGroupToDisplay?: string;
    canReviewResults?: boolean;
}) {
    const { previewMode } = useContext(PreviewModeContext);
    const groupId = useAppSelector((state) => {
        const form = selectForm(state);
        const formType = selectFormType(state);
        if (questionGroupToDisplay) {
            return questionGroupToDisplay;
        } else if (formType === FormType.ASSESSMENT && !previewMode) {
            return form?.questionGroups[0];
        }
        return "";
    });
    const questionGroupIds = useAppSelector(
        selectVisibleQuestionGroupIds,
        shallowEqual
    );
    const startingGroup = questionGroupToDisplay || questionGroupIds[0];
    const startIndex =
        questionGroupIds.findIndex((group) => group === startingGroup) || 0;
    document.documentElement.style.setProperty(
        "--start-point",
        startIndex.toString()
    );
    return (
        <ol className="ScorableQuestionGroupList">
            {questionGroupIds
                .filter(
                    (questionGroupId) =>
                        questionGroupId === groupId || groupId === ""
                )
                .map((questionGroupId) => (
                    <li
                        className="ScorableQuestionGroupListEntry"
                        key={questionGroupId}
                    >
                        <div>
                            <ScorableQuestionGroup
                                id={questionGroupId}
                                isRescoreMode={rescoringEvaluation}
                            />
                            <ScorableQuestionList
                                questionGroupId={questionGroupId}
                                allowEditing={rescoringEvaluation}
                                canReviewResults={canReviewResults}
                            />
                        </div>
                    </li>
                ))}
        </ol>
    );
}
